import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { PhoneMockup } from './PhoneMockup';
import { BoxIcon } from 'lucide-react';
interface FeatureSectionProps {
  badge: string;
  icon: BoxIcon;
  title: string;
  highlight: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
  reverse?: boolean;
  bgColor: string;
  blobColor: string;
}
export function FeatureSection({
  badge,
  icon: Icon,
  title,
  highlight,
  description,
  imageUrl,
  imageAlt,
  reverse = false,
  bgColor,
  blobColor
}: FeatureSectionProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className={`relative py-24 px-6 overflow-hidden ${bgColor}`} data-id="element-30">
      {/* Background decorations */}
      <div className={`absolute ${reverse ? 'left-0' : 'right-0'} top-1/4 w-96 h-96 rounded-full blur-3xl opacity-60 ${blobColor}`} data-id="element-31"></div>

      {/* Sparkle decoration */}
      <div className={`absolute ${reverse ? 'right-20' : 'left-20'} top-32 text-[#15193D]/10 text-5xl`} data-id="element-32">
        ✦
      </div>

      <div className="relative max-w-7xl mx-auto" data-id="element-33">
        <div className={`grid lg:grid-cols-2 gap-16 items-center ${reverse ? 'lg:grid-flow-dense' : ''}`} data-id="element-34">
          {/* Text content */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 30
        }} transition={{
          duration: 0.6
        }} className={`text-center lg:text-left ${reverse ? 'lg:col-start-2' : ''}`} data-id="element-35">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#15193D] text-[#FBF3EA] px-6 py-3 rounded-full mb-8 shadow-lg" data-id="element-36">
              <Icon className="w-5 h-5" data-id="element-37" />
              <span className="font-semibold tracking-wide uppercase text-sm" data-id="element-38">
                {badge}
              </span>
            </div>

            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-[#15193D] mb-8 tracking-tight leading-[1.1]" style={{
            fontFamily: 'Fraunces, serif'
          }} data-id="element-39">
              {title} {highlight}
            </h2>

            <p className="text-xl md:text-2xl text-[#15193D]/80 max-w-xl mx-auto lg:mx-0 font-medium leading-relaxed" data-id="element-40">
              {description}
            </p>
          </motion.div>

          {/* Phone mockup */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={isInView ? {
          opacity: 1,
          scale: 1
        } : {
          opacity: 0,
          scale: 0.9
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className={reverse ? 'lg:col-start-1' : ''} data-id="element-41">
            <PhoneMockup imageUrl={imageUrl} alt={imageAlt} data-id="element-42" />
          </motion.div>
        </div>
      </div>
    </section>;
}